import { useMemo, useState } from "react";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { MetricPoint } from "../lib/api";
import { makeTimeTickFormatter } from "./salience/format";

type RangeKey = "7d" | "30d" | "90d" | "all";

const RANGES: { key: RangeKey; label: string; days: number | null }[] = [
  { key: "7d", label: "7d", days: 7 },
  { key: "30d", label: "30d", days: 30 },
  { key: "90d", label: "90d", days: 90 },
  { key: "all", label: "All", days: null },
];

const SERIES: {
  key: "viewCount" | "likeCount" | "commentCount";
  label: string;
}[] = [
  { key: "viewCount", label: "Views" },
  { key: "likeCount", label: "Likes" },
  { key: "commentCount", label: "Comments" },
];

// Dark-terminal chart palette — shared with the salience ShareTrendChart.
const LINE_COLOUR = "#4f8cff"; // agenda-public accent
const AXIS_COLOUR = "#5a5f9e";
const TICK_FILL = "#9aa0d8";

interface EngagementChartProps {
  metrics: MetricPoint[] | undefined;
  isLoading: boolean;
}

/**
 * Three stacked thin LineCharts (views, likes, comments) sharing a time
 * axis. Range presets (7d/30d/90d/All); default All — newer videos benefit
 * from seeing the post-publish climb in full.
 *
 * No dual-y-axis trickery; each metric is its own panel so scales are
 * read independently. Dark-terminal register (portal-wide design
 * unification) — matches the salience trend charts.
 */
export function EngagementChart({ metrics, isLoading }: EngagementChartProps) {
  const [range, setRange] = useState<RangeKey>("all");

  const filtered = useMemo(() => {
    if (!metrics || metrics.length === 0) return [];
    const cutoffDays = RANGES.find((r) => r.key === range)?.days;
    let rows = metrics
      .map((m) => ({
        ...m,
        ts: new Date(m.fetchedAt).getTime(),
      }))
      .filter((r) => Number.isFinite(r.ts))
      .sort((a, b) => a.ts - b.ts);
    if (cutoffDays != null) {
      const cutoff = Date.now() - cutoffDays * 86_400_000;
      rows = rows.filter((r) => r.ts >= cutoff);
    }
    return rows;
  }, [metrics, range]);

  // Year-aware ticks: "All" on an older video spans years, where year-less
  // "5 Jun" labels were ambiguous.
  const tickFormatter = useMemo(
    () =>
      makeTimeTickFormatter(
        filtered[0]?.ts ?? NaN,
        filtered[filtered.length - 1]?.ts ?? NaN,
      ),
    [filtered],
  );

  return (
    <section className="rounded-xl border border-white/10 bg-terminal p-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-terminal-ink-muted">
          Engagement
        </h2>
        <div className="flex gap-0.5 rounded-md border border-white/10 bg-terminal-panel p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              className={`rounded px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                range === r.key
                  ? "bg-terminal-elevated text-terminal-ink"
                  : "text-terminal-ink-muted hover:text-terminal-ink"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {SERIES.map((s) => (
            <div
              key={s.key}
              className="h-[100px] animate-pulse rounded-lg bg-terminal-panel/50"
            />
          ))}
        </div>
      ) : !metrics || metrics.length === 0 ? (
        <div className="rounded-lg border border-white/5 bg-terminal-panel/40 p-5 text-sm text-terminal-ink-muted">
          No view or like history for this video yet. We record these
          regularly, so a chart will appear here once we have a few readings.
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-white/5 bg-terminal-panel/40 p-5 text-sm text-terminal-ink-muted">
          No metrics in the last {RANGES.find((r) => r.key === range)?.label}
          . Try a wider range.
        </div>
      ) : (
        <div className="space-y-3">
          {SERIES.map((s) => {
            const latest = filtered[filtered.length - 1][s.key];
            return (
              <div
                key={s.key}
                className="rounded-lg border border-white/10 bg-terminal-panel p-3"
              >
                <div className="mb-1 flex items-baseline justify-between px-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-terminal-ink-muted">
                    {s.label}
                  </span>
                  <span className="font-mono text-sm font-semibold tabular-nums text-terminal-ink">
                    {formatNumber(latest)}
                  </span>
                </div>
                <div className="relative h-[100px] w-full">
                  <div className="absolute inset-0">
                  <ResponsiveContainer>
                    <LineChart
                      data={filtered}
                      margin={{ top: 6, right: 8, bottom: 0, left: 0 }}
                    >
                      <XAxis
                        dataKey="ts"
                        type="number"
                        scale="time"
                        domain={["dataMin", "dataMax"]}
                        tickFormatter={tickFormatter}
                        stroke={AXIS_COLOUR}
                        tick={{ fontSize: 10, fill: TICK_FILL }}
                        tickLine={false}
                        axisLine={false}
                        minTickGap={32}
                      />
                      <YAxis
                        domain={["dataMin", "dataMax"]}
                        stroke={AXIS_COLOUR}
                        tick={{ fontSize: 10, fill: TICK_FILL }}
                        tickFormatter={formatNumber}
                        tickLine={false}
                        axisLine={false}
                        width={36}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#21246a",
                          border: "1px solid rgba(255,255,255,0.15)",
                          borderRadius: 6,
                          fontSize: 12,
                          padding: "4px 8px",
                          color: "#eef0ff",
                        }}
                        labelFormatter={(ts) =>
                          new Date(ts).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "2-digit",
                          })
                        }
                        formatter={(value: number) => [
                          formatNumber(value),
                          s.label,
                        ]}
                      />
                      <Line
                        type="monotone"
                        dataKey={s.key}
                        stroke={LINE_COLOUR}
                        strokeWidth={1.75}
                        dot={false}
                        activeDot={{
                          r: 4,
                          fill: "#aab2ff",
                          stroke: "#0f1036",
                          strokeWidth: 1.5,
                        }}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function formatNumber(n: number): string {
  if (n >= 1_000_000)
    return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(0)}K`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString("en-GB");
}

